/**
 * Trace Explorer API client.
 *
 * Mirrors ``backend/src/hub/routes/trace.py``. The server walks
 * the causal graph from any anchor (delivery / message / execution /
 * node_execution) and returns ``{nodes, edges}`` ready for ReactFlow.
 */
import { api } from "./api";

export type TraceNodeKind =
  | "message"
  | "execution"
  | "node_execution"
  | "delivery"
  | "ingress_source";

export type TraceEdgeKind =
  | "received"
  | "triggered"
  | "contains"
  | "emitted"
  | "chained";

export interface TraceNode {
  /** Globally unique within the response: ``"<kind>:<uuid>"``. */
  id: string;
  kind: TraceNodeKind;
  /** Short human-readable label for the node body. */
  label: string;
  /** Minimal entity data — enough for the sidebar; full row is
   *  fetched via the per-entity detail page if the operator
   *  clicks "Open in <entity>". */
  data: Record<string, unknown>;
}

export interface TraceEdge {
  source: string;
  target: string;
  kind: TraceEdgeKind;
}

export interface TraceResponse {
  anchor: { type: string; id: string };
  nodes: TraceNode[];
  edges: TraceEdge[];
  /** ``true`` when BFS hit ``back_depth`` with more upstream to
   *  explore — the UI surfaces a "more upstream" affordance. */
  truncated_back: boolean;
  truncated_forward: boolean;
}

export interface TraceQuery {
  anchor_type: "message" | "execution" | "node_execution" | "delivery";
  anchor_id: string;
  back_depth?: number;
  forward_depth?: number;
}

export const traceApi = {
  get: (q: TraceQuery) => {
    const params = new URLSearchParams({
      anchor_type: q.anchor_type,
      anchor_id: q.anchor_id,
    });
    if (q.back_depth !== undefined) params.set("back_depth", String(q.back_depth));
    if (q.forward_depth !== undefined)
      params.set("forward_depth", String(q.forward_depth));
    return api.get<TraceResponse>(`/trace?${params.toString()}`);
  },
};
